import { SupportPermissions } from "@redotech/account-common/permissions";
import { Schema } from "effect";
import {
  ChannelThreadId,
  ConversationId,
  DraftMessageId,
  ShopperAiTagId,
  SupportTicketId,
} from "../../ids/definitions";
import { MongoUserId } from "../../ids/mongo-ids";
import { AiActivityNotification } from "../shopper-ai/ai-activity-notification";
import { Message } from "../shopper-ai/conversation/message";
import {
  SupportTicketListItemResponse,
  SupportTicketPriority,
  SupportTicketStatus,
} from "../shopper-ai/conversation/support-ticket";
import { ChannelThread } from "../shopper-ai/conversation/thread";
import { ConversationTimelineEvent } from "../shopper-ai/conversation/timeline-event/timeline-event";
import { PresenceNotification } from "../team/presence-notification";
import { TaskRealtimeNotification } from "../team/task-notification";
import { UserNotification } from "../team/user-notification";
import { SupportTicketBulkActionNotification } from "./support-ticket-bulk-action-notification";

/**
 * Schema transform: Uint8Array (UTF-8) <-> string
 * Effect doesn't have a built-in for this, only for Base64/Hex encodings.
 * Decodes bytes to string, encodes string to bytes.
 */
export const StringFromUint8Array = Schema.transform(
  Schema.Uint8ArrayFromSelf,
  Schema.String,
  {
    strict: true,
    decode: (bytes) => new TextDecoder().decode(bytes),
    encode: (str) => new TextEncoder().encode(str),
  },
).annotations({ identifier: "StringFromUint8Array" });

export const MessageCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("messageCreated"),
  supportTicketId: SupportTicketId,
  message: Message,
});
export type MessageCreatedNotification = typeof MessageCreatedNotification.Type;

export const SupportTicketCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("supportTicketCreated"),
  supportTicket: SupportTicketListItemResponse,
});
export type SupportTicketCreatedNotification =
  typeof SupportTicketCreatedNotification.Type;

export const SupportTicketUpdatedNotification = Schema.Struct({
  _tag: Schema.Literal("supportTicketUpdated"),
  supportTicketId: SupportTicketId,
  updateType: Schema.Literal(
    "status_changed",
    "assigned",
    "priority_changed",
    "read",
    "tag_added",
    "tag_removed",
  ),
  // Include the updated value for each update type
  newStatus: Schema.optional(SupportTicketStatus),
  newAssignee: Schema.optional(Schema.NullOr(MongoUserId)),
  newPriority: Schema.optional(Schema.NullOr(SupportTicketPriority)),
  readBy: Schema.optional(MongoUserId),
  newSnoozedUntil: Schema.optional(Schema.NullOr(Schema.Date)),
  tagId: Schema.optional(ShopperAiTagId),
});
export type SupportTicketUpdatedNotification =
  typeof SupportTicketUpdatedNotification.Type;

export const ThreadCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("threadCreated"),
  supportTicketId: SupportTicketId,
  thread: ChannelThread,
});
export type ThreadCreatedNotification = typeof ThreadCreatedNotification.Type;

export const TimelineEventCreatedNotification = Schema.Struct({
  _tag: Schema.Literal("timelineEventCreated"),
  supportTicketId: SupportTicketId,
  event: ConversationTimelineEvent,
});
export type TimelineEventCreatedNotification =
  typeof TimelineEventCreatedNotification.Type;

export const MessageUpdatedNotification = Schema.Struct({
  _tag: Schema.Literal("messageUpdated"),
  supportTicketId: SupportTicketId,
  message: Message,
});
export type MessageUpdatedNotification = typeof MessageUpdatedNotification.Type;

/**
 * Fired when one or more teammates are @-mentioned in an internal note.
 * Broadcast to the full team; each client filters by checking whether its
 * current user id is in `mentionedMongoUserIds` to decide whether to surface
 * a toast / notification.
 */
export const ConversationMentionedNotification = Schema.Struct({
  _tag: Schema.Literal("conversationMentioned"),
  conversationId: ConversationId,
  mentionedMongoUserIds: Schema.Array(MongoUserId),
  mentionedByMongoUserId: Schema.NullOr(MongoUserId),
  mentionedByName: Schema.NullOr(Schema.String),
  messagePreview: Schema.String,
});
export type ConversationMentionedNotification =
  typeof ConversationMentionedNotification.Type;

/**
 * Lifecycle stages of an outbound merchant send, surfaced so the composer can
 * show "Waiting…" (inside the undo window, Temporal sleeping) → "Sending…"
 * (window elapsed, dispatching) → terminal. `sent` is also implied by the
 * `messageCreated` notification (which replaces the optimistic draft); `error`
 * lets the UI surface a failed send with a retry affordance instead of silently
 * rolling the draft back.
 */
export const DraftSendStage = Schema.Literal(
  "waiting",
  "sending",
  "sent",
  "error",
);
export type DraftSendStage = typeof DraftSendStage.Type;

/**
 * Fired by the outbound send workflow as a send moves through its lifecycle.
 * Scoped to a single draft so the composer/transcript can render the live
 * stage without polling. There is no persisted draft column for the stage — it
 * is a transient send-lifecycle signal the client holds in its optimistic
 * collection.
 */
export const DraftSendStateChangedNotification = Schema.Struct({
  _tag: Schema.Literal("draftSendStateChanged"),
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
  draftId: DraftMessageId,
  stage: DraftSendStage,
  /**
   * ISO timestamp the send is scheduled to dispatch (end of the undo window).
   * Present for `waiting` so the client can drive its countdown from the
   * server's clock rather than the moment the button was clicked.
   */
  sendAt: Schema.optional(Schema.String),
  /** Short, display-safe reason. Present for `error`. */
  errorReason: Schema.optional(Schema.String),
  /** Human-readable message shown to the merchant in the composer error toast. Falls back to generic copy when absent. */
  userFacingErrorMessage: Schema.optional(Schema.String),
});
export type DraftSendStateChangedNotification =
  typeof DraftSendStateChangedNotification.Type;

/**
 * Fired when a closed ticket produced knowledge proposals. Every subscriber on
 * the account receives it, so it carries ids and a label, never article bodies.
 */
export const KnowledgeSuggestionsGeneratedNotification = Schema.Struct({
  _tag: Schema.Literal("knowledgeSuggestionsGenerated"),
  supportTicketId: SupportTicketId,
  /** Only the user who closed the ticket is notified; null means nobody is. */
  notifyMongoUserId: Schema.NullOr(MongoUserId),
  /** `smk_` ids; the recipient fetches the drafts it is allowed to see. */
  suggestionIds: Schema.Array(Schema.String),
  /** One short line for the toast, so it can render before the fetch lands. */
  preview: Schema.String,
});
export type KnowledgeSuggestionsGeneratedNotification =
  typeof KnowledgeSuggestionsGeneratedNotification.Type;

export const SupportTicketNotification = Schema.Union(
  MessageCreatedNotification,
  MessageUpdatedNotification,
  SupportTicketCreatedNotification,
  SupportTicketUpdatedNotification,
  ThreadCreatedNotification,
  TimelineEventCreatedNotification,
  ConversationMentionedNotification,
  DraftSendStateChangedNotification,
  KnowledgeSuggestionsGeneratedNotification,
);
export type SupportTicketNotification = typeof SupportTicketNotification.Type;

export const SupportTicketNotificationJson = Schema.parseJson(
  SupportTicketNotification,
);

export const SupportTicketNotificationFromBytes = Schema.compose(
  StringFromUint8Array,
  SupportTicketNotificationJson,
);

export type SupportTicketNotificationTag = SupportTicketNotification["_tag"];

export const supportTicketNotificationRequiredPermissions: Record<
  SupportTicketNotificationTag,
  readonly string[]
> = {
  messageCreated: [SupportPermissions.VIEW_SUPPORT],
  messageUpdated: [SupportPermissions.VIEW_SUPPORT],
  supportTicketCreated: [SupportPermissions.VIEW_SUPPORT],
  supportTicketUpdated: [SupportPermissions.VIEW_SUPPORT],
  threadCreated: [SupportPermissions.VIEW_SUPPORT],
  timelineEventCreated: [SupportPermissions.VIEW_SUPPORT],
  conversationMentioned: [SupportPermissions.VIEW_SUPPORT],
  draftSendStateChanged: [SupportPermissions.VIEW_SUPPORT],
  knowledgeSuggestionsGenerated: [SupportPermissions.VIEW_SUPPORT],
};

export const PublishNotificationResponse = Schema.Struct({
  success: Schema.Literal(true),
});
export type PublishNotificationResponse =
  typeof PublishNotificationResponse.Type;

export const AllStreamNotification = Schema.Union(
  SupportTicketNotification,
  PresenceNotification,
  AiActivityNotification,
  SupportTicketBulkActionNotification,
  UserNotification,
  TaskRealtimeNotification,
);
export type AllStreamNotification = typeof AllStreamNotification.Type;

export const AllStreamNotificationJson = Schema.parseJson(
  AllStreamNotification,
);

export type AllStreamNotificationTag = AllStreamNotification["_tag"];

// ---------------------------------------------------------------------------
// Notification constructors
// ---------------------------------------------------------------------------

export const SupportTicketNotificationEvent = {
  supportTicketCreated: (
    supportTicket: SupportTicketListItemResponse,
  ): SupportTicketCreatedNotification => ({
    _tag: "supportTicketCreated",
    supportTicket,
  }),

  supportTicketUpdated: {
    statusChanged: (
      supportTicketId: SupportTicketId,
      newStatus: SupportTicketStatus,
      newSnoozedUntil?: Date | null,
    ): SupportTicketUpdatedNotification => ({
      _tag: "supportTicketUpdated",
      supportTicketId,
      updateType: "status_changed",
      newStatus,
      newSnoozedUntil,
    }),

    assigned: (
      supportTicketId: SupportTicketId,
      newAssignee?: MongoUserId | null,
    ): SupportTicketUpdatedNotification => ({
      _tag: "supportTicketUpdated",
      supportTicketId,
      updateType: "assigned",
      newAssignee,
    }),

    priorityChanged: (
      supportTicketId: SupportTicketId,
      newPriority?: SupportTicketPriority | null,
    ): SupportTicketUpdatedNotification => ({
      _tag: "supportTicketUpdated",
      supportTicketId,
      updateType: "priority_changed",
      newPriority,
    }),

    read: (
      supportTicketId: SupportTicketId,
      readBy: MongoUserId,
    ): SupportTicketUpdatedNotification => ({
      _tag: "supportTicketUpdated",
      supportTicketId,
      updateType: "read",
      readBy,
    }),

    tagAdded: (
      supportTicketId: SupportTicketId,
      tagId: ShopperAiTagId,
    ): SupportTicketUpdatedNotification => ({
      _tag: "supportTicketUpdated",
      supportTicketId,
      updateType: "tag_added",
      tagId,
    }),

    tagRemoved: (
      supportTicketId: SupportTicketId,
      tagId: ShopperAiTagId,
    ): SupportTicketUpdatedNotification => ({
      _tag: "supportTicketUpdated",
      supportTicketId,
      updateType: "tag_removed",
      tagId,
    }),
  },

  threadCreated: (
    supportTicketId: SupportTicketId,
    thread: ChannelThread,
  ): ThreadCreatedNotification => ({
    _tag: "threadCreated",
    supportTicketId,
    thread,
  }),

  messageCreated: (
    supportTicketId: SupportTicketId,
    message: Message,
  ): MessageCreatedNotification => ({
    _tag: "messageCreated",
    supportTicketId,
    message,
  }),

  messageUpdated: (
    supportTicketId: SupportTicketId,
    message: Message,
  ): MessageUpdatedNotification => ({
    _tag: "messageUpdated",
    supportTicketId,
    message,
  }),

  timelineEventCreated: (
    supportTicketId: SupportTicketId,
    event: ConversationTimelineEvent,
  ): TimelineEventCreatedNotification => ({
    _tag: "timelineEventCreated",
    supportTicketId,
    event,
  }),

  knowledgeSuggestionsGenerated: (input: {
    supportTicketId: SupportTicketId;
    notifyMongoUserId: MongoUserId | null;
    suggestionIds: readonly string[];
    preview: string;
  }): KnowledgeSuggestionsGeneratedNotification => ({
    _tag: "knowledgeSuggestionsGenerated",
    supportTicketId: input.supportTicketId,
    notifyMongoUserId: input.notifyMongoUserId,
    suggestionIds: input.suggestionIds,
    preview: input.preview,
  }),

  draftSendStateChanged: (input: {
    supportTicketId: SupportTicketId;
    threadId: ChannelThreadId;
    draftId: DraftMessageId;
    stage: DraftSendStage;
    sendAt?: string;
    errorReason?: string;
    userFacingErrorMessage?: string;
  }): DraftSendStateChangedNotification => ({
    _tag: "draftSendStateChanged",
    supportTicketId: input.supportTicketId,
    threadId: input.threadId,
    draftId: input.draftId,
    stage: input.stage,
    ...(input.sendAt !== undefined ? { sendAt: input.sendAt } : {}),
    ...(input.errorReason !== undefined
      ? { errorReason: input.errorReason }
      : {}),
    ...(input.userFacingErrorMessage !== undefined
      ? { userFacingErrorMessage: input.userFacingErrorMessage }
      : {}),
  }),
} as const;
